import type { SheetTextGeometry } from "building-types/views/sheetGeometryTypes";
import colors from "common/constants/colorsOld";
import { SVG_UNITS_TO_INCHES } from "common/constants/pdfSettings";
import { ZERO_INCHES, ZERO_POINTS } from "common/constants/scaleFactors";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { assert, assertIsNotNull } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { numAdd, numMul, sheetPointsToInches } from "common/utils/nominalNumbers";
import { inches } from "doc/pdf/drawingLayout";
import { styleSheetMerge, styleSheetNew } from "doc/plans/planStyles";
import { typeSelector } from "doc/plans/primitives/primitiveStyles";
import sheetGeometryListAABB2, {
  sheetGeometryAABB2,
  allSheetGeometryTypeAABB2Filter,
} from "doc/plans/sheetGeometryAABB2";
import { aabb2New } from "geom/aabb2Types";
import { aabb2Width } from "geom/aabb2Types";
import { aabb2Height } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import type { GeometrySheetNode, TableCellSheetNode, TableSheetNode } from "lib/house/plans/sheetNode";
import { SheetNodeType } from "lib/house/plans/sheetNode";
import { SVGBoundingBoxProvider } from "svg/svgBoundingBoxProvider";
import { makeGeometrySheetNode, makeSVGSheetNode, makeTextSheetNode } from "./geometrySheetNode";
import { makeSheetNodeStyleSheet, sheetNodeGetStyle } from "./nodeStyles";
import type { SheetNodeStyleSheet } from "./nodeStylesTypes";
import { TableCellContentType } from "./tableCellTypes";
import type { TableCellProps, TextCellContents, SVGCellContents, GroupCellContents } from "./tableCellTypes";
import { validateTableCellPropsArray } from "./tableCellValidation";

// this is a custom type which will let us convert numbers to string inside of the table
// cell constructor
function makeTextTableCell(contents: TextCellContents, style: SheetNodeStyleSheet = {}): TableCellSheetNode {
  const { text } = contents;
  return {
    type: SheetNodeType.TABLE_CELL,
    x: inches(0),
    y: inches(0),
    style,
    contentType: TableCellContentType.TEXT,
    contents: makeTextSheetNode({
      x: 0,
      y: 0,
      ...contents,
      text: text === undefined || text === "undefined" || text === "" ? " " : String(text),
    }),
  };
}

function makeSVGTableCell(contents: SVGCellContents, style: SheetNodeStyleSheet = {}): TableCellSheetNode {
  return {
    type: SheetNodeType.TABLE_CELL,
    x: inches(0),
    y: inches(0),
    style,
    contentType: TableCellContentType.SVG,
    contents: makeSVGSheetNode({
      x: 0,
      y: 0,
      ...contents,
    }),
  };
}

function makeGroupTableCell(contents: GroupCellContents, style: SheetNodeStyleSheet = {}): TableCellSheetNode {
  return {
    type: SheetNodeType.TABLE_CELL,
    x: inches(0),
    y: inches(0),
    style,
    contentType: TableCellContentType.GROUP,
    contents: {
      type: SheetNodeType.GROUP,
      children: contents.children.map((child) =>
        makeGeometrySheetNode({
          x: 0,
          y: 0,
          ...child,
        }),
      ),
    },
  };
}

export function makeTableCell(cellProps: TableCellProps | null) {
  assertIsNotNull(cellProps);
  const { cellStyle, contentType, contents } = cellProps;
  const style = cellStyle
    ? styleSheetNew({
        [typeSelector(SheetNodeType.TABLE_CELL)]: cellStyle,
      })
    : undefined;
  switch (contentType) {
    case TableCellContentType.GROUP:
      return makeGroupTableCell(contents, style);
    case TableCellContentType.SVG:
      return makeSVGTableCell(contents, style);
    case TableCellContentType.TEXT:
      return makeTextTableCell(contents, style);
    default:
      assertUnreachable(contents);
  }
}

const textBlockStyleSheet = makeSheetNodeStyleSheet({
  [SheetNodeType.TABLE]: {
    border: false,
    strokeColor: colors.transparent,
  },
  [SheetNodeType.TABLE_CELL]: {
    border: false,
    padding: ZERO_POINTS,
  },
});

interface TextBlockProps extends Omit<TableSheetNode, "type" | "rows"> {
  paragraphs: TextCellContents[];
}

export function makeTextBlock({ x, y, width, height, style, paragraphs }: TextBlockProps): TableSheetNode {
  return {
    type: SheetNodeType.TABLE,
    x,
    y,
    width,
    height,
    style: style ? styleSheetMerge(textBlockStyleSheet, style) : textBlockStyleSheet,
    rows: paragraphs.map((contents) => [makeTextTableCell(contents)]),
  };
}

interface TableProps extends Omit<TableSheetNode, "type" | "rows"> {
  rows: (TableCellProps | null)[][];
}

export function makeTable({ x, y, width, height, style, rows, columnWidths, title }: TableProps): TableSheetNode {
  const rowCells: TableCellSheetNode[][] = [];

  // Allow for inline conditional rendering of cells and validate them
  for (let row of rows) {
    row = row.filter(Boolean);
    // Validate and filter out invalid cells to prevent errors
    const validCells = validateTableCellPropsArray(row, "makeTable");
    rowCells.push(validCells.map(makeTableCell));
  }

  // Create table with separate title property
  return {
    type: SheetNodeType.TABLE,
    x,
    y,
    width,
    height,
    style,
    rows: rowCells,
    columnWidths,
    title,
  };
}

export function tableCellAABB2(cell: TableCellSheetNode): AABB2 {
  const { x = 0, y = 0, width, height } = cell;
  if (width !== undefined && height !== undefined) {
    // this is a fully reesolved cell, and we don't need to compute content bounds
    return aabb2New(x, y, x + width, y + height);
  }

  const style = sheetNodeGetStyle(cell);
  const padding = style?.padding ?? ZERO_POINTS;
  const verticalPadding = style?.verticalPadding ?? ZERO_POINTS;

  const contentAABB = tableCellContentAABB2(cell);
  const contentWidth = aabb2Width(contentAABB) as SheetInches;
  const contentHeight = aabb2Height(contentAABB) as SheetInches;

  let cellWidth = width;
  if (cellWidth === undefined) {
    cellWidth = numAdd(contentWidth, sheetPointsToInches(numMul(padding, 2)));
  }

  let cellHeight = height;
  if (cellHeight === undefined) {
    cellHeight = numAdd(contentHeight, sheetPointsToInches(numAdd(numMul(padding, 2), verticalPadding)));
  }

  return aabb2New(x, y, x + cellWidth, y + cellHeight);
}

/**
 * Returns the AABB of cell contents in SheetPoints
 */
export function tableCellContentAABB2(cell: TableCellSheetNode): AABB2 {
  if (!cell.contents) {
    return aabb2New(0, 0, 0, 0);
  }

  switch (cell.contentType) {
    case TableCellContentType.TEXT: {
      const boxWidth = sheetNodeGetStyle(cell)?.boxWidth ?? ZERO_INCHES;
      return tableCellTextContentAABB2(cell.contents.geometry, boxWidth);
    }
    case TableCellContentType.SVG: {
      const { path } = cell.contents.geometry;
      const bb = SVGBoundingBoxProvider.instance.getAABB2FromSVGString(
        path,
        0,
        0,
        0,
        SVG_UNITS_TO_INCHES,
        SVG_UNITS_TO_INCHES,
      );
      if (!bb) {
        return aabb2New(0, 0, 0, 0);
      }
      return bb;
    }
    case TableCellContentType.GROUP: {
      if (cell.contents.children.length === 0) {
        return aabb2New(0, 0, 0, 0);
      }
      const geometryList = {
        geometry: cell.contents.children.map((child) => {
          assert(child.type === SheetNodeType.GEOMETRY);
          return (child as GeometrySheetNode).geometry;
        }),
      };
      return sheetGeometryListAABB2(geometryList, 0, allSheetGeometryTypeAABB2Filter);
    }
    default: {
      assertUnreachable(cell);
    }
  }
}

/**
 * Text AABBs are in SheetInches. We need to convert them to SheetPoints because for
 * the time being all SheetNode Geometry is in inches
 */
function tableCellTextContentAABB2(geometry: SheetTextGeometry, boxWidth?: SheetInches) {
  if (boxWidth !== undefined) {
    geometry = {
      ...geometry,
      boxWidth: geometry.boxWidth ?? boxWidth,
    };
  }
  const bb = sheetGeometryAABB2(geometry);
  if (!bb) {
    return aabb2New(0, 0, 0, 0);
  }
  return bb;
}
